import { useTranslation } from 'next-i18next';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, LoadingCircle } from '@bridebook/ui';
import Paragraph from '@bridebook/web/components/settings/paragraph/paragraph';
import { RsvpError } from '@bridebook/web/components/wedding-website/rsvp/rsvp-list/partials/rsvp-error';
import { RsvpTitle } from '@bridebook/web/components/wedding-website/rsvp/rsvp-list/partials/rsvp-title';
import { AnswerTypeField } from '@bridebook/web/components/wedding-website/rsvp/question-form/sections/answer-type-field';
import { EventSelector } from '@bridebook/web/components/events/selector/event-selector';
import { PopularQuestions } from '@bridebook/web/components/wedding-website/rsvp/question-form/sections/popular-questions';
import { QuestionField } from '@bridebook/web/components/wedding-website/rsvp/question-form/sections/question-field';
import { useUnifiedEvents } from '@bridebook/web/lib/events/use-unified-events';
import { useEventOptionsWithHandlers } from '@bridebook/web/lib/rsvp/hooks/use-event-options-with-handlers';
import { useUpdateWeddingRsvp } from '@bridebook/web/lib/rsvp/query/hooks/wedding/use-update-wedding-rsvp';
import { useWeddingRsvp } from '@bridebook/web/lib/rsvp/query/hooks/wedding/use-wedding-rsvp';
import { RsvpQuestion, RsvpQuestionFormValues } from '@bridebook/web/lib/rsvp/types';
import styles from '@bridebook/web/components/wedding-website/wedding-details/sections/rsvp/add-edit-question-form-content.style';
import { useConfirmExitModal } from '@bridebook/web/lib/rsvp/hooks/use-confirm-exit-modal';
import { cn, Flex } from '@bridebook/aura';

interface IProps extends PropsWithChildren {
  questionId?: string;
  className?: string;
}
export const AddEditQuestionFormContent = ({ questionId, className }: IProps) => {
  const { t } = useTranslation('weddingWebsite');

  const isNewEntry = !questionId;

  const { unifiedEvents, isLoading: isUnifiedEventsLoading } = useUnifiedEvents();
  const { data: weddingRsvp, isLoading: isWeddingRsvpQueryLoading } = useWeddingRsvp();
  const { isLoading: isWeddingRsvpUpdateLoading } = useUpdateWeddingRsvp();

  const isDataLoaded =
    weddingRsvp && !isWeddingRsvpQueryLoading && !isNewEntry && typeof questionId === 'string';

  const form = useFormContext<RsvpQuestionFormValues>();
  const {
    control,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = form;

  useEffect(() => {
    if (!isDataLoaded || !weddingRsvp) return;
    const entry = weddingRsvp.questions.find((q) => q.id === questionId);

    if (!entry) return;

    reset({
      question: entry.question,
      type: entry.type,
      events: entry.events,
      options: entry.type === 'list' ? (entry.options ?? []) : [],
    });
  }, [questionId, isDataLoaded, reset, weddingRsvp]);

  // listen for changes to question and options to update pill list selection
  watch('type');
  watch('question');
  watch('options');

  const onClickPopularQuestion = useCallback(
    ({ question, type, options }: RsvpQuestion) => {
      setValue('question', question);
      setValue('type', type);
      if (type === 'list') {
        setValue('options', options);
      }
    },
    [setValue],
  );

  useConfirmExitModal({
    id: 'ADD_RSVP_CONFIRM_EXIT',
    isDirty: isDirty && !isSubmitting && !isWeddingRsvpUpdateLoading,
    title: t('rsvp.modal.exit.title'),
    text: t('rsvp.modal.exit.text'),
    primaryButtonText: t('rsvp.modal.exit.abort'),
    secondaryButtonText: t('rsvp.modal.exit.confirm'),
  });

  const { eventOptions, handleSelectCheckbox, handleSelectAllCheckbox } =
    useEventOptionsWithHandlers({
      isLoading: isUnifiedEventsLoading,
      unifiedEvents,
      getValues,
      setValue,
    });

  if (isWeddingRsvpQueryLoading) {
    return <LoadingCircle color="space" />;
  }

  const hasEvents = eventOptions.length > 0;

  return (
    <Flex className={cn('flex-col gap-4', className)}>
      <PopularQuestions currentQuestion={getValues()} onAdd={onClickPopularQuestion} />

      <Flex className="flex-col">
        <Box style={styles.field}>
          <RsvpTitle>{t('rsvp.form.question.title')}</RsvpTitle>
          <QuestionField control={control} error={errors.question?.message} />
        </Box>

        <Box style={styles.field}>
          <RsvpTitle>{t('rsvp.form.answer.title')}</RsvpTitle>
          <AnswerTypeField control={control} error={errors.options?.message} />
        </Box>

        <RsvpTitle>{t('rsvp.form.events.title')}*</RsvpTitle>
        {errors.events && <RsvpError>{errors.events.message}</RsvpError>}
        {!hasEvents && <Paragraph>{t('rsvp.form.events.missing')}</Paragraph>}
        {hasEvents && (
          <EventSelector
            control={control}
            eventOptions={eventOptions}
            handleSelectCheckbox={handleSelectCheckbox}
            handleSelectAllCheckbox={handleSelectAllCheckbox}
          />
        )}
      </Flex>
    </Flex>
  );
};
